import { ChangeEvent, useMemo } from "react";
import Form from "react-bootstrap/Form";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import classNames from "classnames";
import { ClickableIcon } from "../clickable-icon/clickable-icon";
import ReactDatepicker from "react-datepicker";
import { IdwFormLabel } from "../idw-form-label/idw-form-label";
import styles from "./date-input-form-group.less";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";

type DateInputFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    className?: string;
    automationId?: string;
    ariaLabel?: string;
    placeholder?: string;
    label: string;
    value?: Date;
    typedDate?: string;
    onChange: (date: Date) => void;
    onChangeRawDateOnly?: (dateString: string) => void;
    layout?: LayoutTypes;
    disabled: boolean;
    isRequired: boolean;
    afterInputIcon?: IconDefinition;
    afterInputIconOnClick?: () => void;
    shouldCloseOnSelect: boolean;
    showMonthDropdown: boolean;
    showYearDropdown: boolean;
    showMonthYearPicker: boolean;
    showTimeSelect?: boolean;
    dateFormat: string;
    minDate: Date;
    maxDate: Date;
    isClearable?: boolean;
};

export const DateInputFormGroup = (props: DateInputFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        ariaLabel,
        placeholder,
        label,
        layout,
        value,
        typedDate,
        onChange,
        onChangeRawDateOnly,
        disabled,
        isRequired,
        afterInputIcon,
        afterInputIconOnClick,
        shouldCloseOnSelect,
        showMonthDropdown,
        showYearDropdown,
        showMonthYearPicker,
        showTimeSelect,
        dateFormat,
        minDate,
        maxDate,
        isClearable = false
    } = props;

    const handleOnChange = (newDateString: string) => {
        if (newDateString) {
            const newDate = new Date(newDateString);
            onChange(newDate);
        } else if (isClearable) {
            onChange(null);
        }
    };

    const handleRawChange = onChangeRawDateOnly ? (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (typeof value !== "undefined" && value.length < 10 && (value === "" || /^[0-9,. /-]+$/.test(value))) {
            onChangeRawDateOnly(value);
        }
    } : undefined;

    const formGroupClassName = useMemo(() => {
        return layout === "column" ? styles.dateInputFormGroupColumn : styles.dateInputFormGroupRow;
    }, [layout]);

    return (
        <Form.Group controlId={controlId} className={classNames(formGroupClassName, className)}>
            <IdwFormLabel
                isRequired={isRequired}
                labelWidth={labelWidth}
                label={label}
                layout={layout}
            />
            <Form.Control
                as={ReactDatepicker}
                className={className}
                automation-id={automationId}
                aria-label={ariaLabel}
                value={typedDate}
                selected={value}
                onChange={handleOnChange as any}
                onChangeRaw={handleRawChange as any}
                autoComplete={"off"}
                disabled={disabled}
                placeholderText={placeholder}
                shouldCloseOnSelect={shouldCloseOnSelect}
                dateFormat={dateFormat}
                showYearDropdown={showYearDropdown}
                showMonthDropdown={showMonthDropdown}
                showMonthYearPicker={showMonthYearPicker}
                showTimeSelect={showTimeSelect}
                maxDate={maxDate}
                minDate={minDate}
                required={isRequired}
                data-lpignore="true"
                isClearable={isClearable}
                clearButtonClassName={styles.clearButton}
            />
            {afterInputIcon && afterInputIconOnClick && (
                <div>
                    <ClickableIcon controlId={controlId} disabled={disabled} icon={afterInputIcon} onClick={afterInputIconOnClick} className={styles.afterInputIcon} />
                </div>
            )}
        </Form.Group>
    );
};